/**
 * Labor standards — the page.
 *
 * Recipes: one per product, a work center on every step. This file is the
 * composition: the catalog with its search, and the three dialogs (form,
 * upload, preview). Data and writes live in controller/use-labor-standards,
 * the form's state in controller/use-standard-form, the pieces in ui/.
 */
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import UploadFileIcon from "@mui/icons-material/CloudUploadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { Box, CircularProgress, Grid2, IconButton, InputAdornment, Snackbar, TextField, Typography } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";

import { LaborStandard } from "../../../services/labor-standards-api";
import { EmptyState, GhostButton, planner, PlannerHeader, PlannerPage, PrimaryButton, SectionLabel } from "../planner-ui"; // eslint-disable-line sort-imports
import { filterStandards, useLaborStandards } from "./controller/use-labor-standards";
import { useStandardForm } from "./controller/use-standard-form";
import LaborStandardPreviewDialog from "./LaborStandardPreviewDialog";
import StandardCard from "./ui/standard-card";
import StandardFormDialog from "./ui/standard-form";
import { fieldSx } from "./ui/styles";
import UploadLaborStandardDialog from "./UploadLaborStandardDialog";

const searchFieldSx = {
	...fieldSx,
	minWidth: 280,
	"& .MuiOutlinedInput-root": {
		...fieldSx["& .MuiOutlinedInput-root"],
		...planner.type.body,
	},
} as const;

const LaborStandards: React.FC = () => {
	const data = useLaborStandards();
	const form = useStandardForm({
		products: data.products,
		operationTypes: data.operationTypes,
		save: data.saveStandard,
		notify: data.notify,
	});

	const [expandedOperations, setExpandedOperations] = useState<Record<number, boolean>>({});
	const [searchQuery, setSearchQuery] = useState("");
	const [showUploadDialog, setShowUploadDialog] = useState(false);
	const [previewStandard, setPreviewStandard] = useState<LaborStandard | null>(null);

	const filteredStandards = useMemo(
		() => filterStandards(data.laborStandards, searchQuery, data.productById),
		[data.laborStandards, searchQuery, data.productById]
	);
	const hasSearch = searchQuery.trim().length > 0;
	const existingForProduct = useMemo(
		() => (form.selectedProduct ? data.standardsForProduct(form.selectedProduct.id) : []),
		[form.selectedProduct, data]
	);

	const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value), []);
	const clearSearch = useCallback(() => setSearchQuery(""), []);
	const openUpload = useCallback(() => setShowUploadDialog(true), []);
	const closeUpload = useCallback(() => setShowUploadDialog(false), []);
	const handleUploadSuccess = useCallback(() => {
		setShowUploadDialog(false);
		data.reloadStandards();
		data.notify("Standards uploaded successfully", "success");
	}, [data]);
	const closePreview = useCallback(() => setPreviewStandard(null), []);
	const toggleExpand = useCallback((standardId: number) => {
		setExpandedOperations((prev) => ({ ...prev, [standardId]: !prev[standardId] }));
	}, []);
	const deleteStandard = useCallback((id: number) => void data.deleteStandard(id), [data]);

	return (
		<PlannerPage>
			<PlannerHeader
				title="Standards"
				subtitle="Recipes: one per product, a work center on every step"
				actions={
					<>
						<TextField
							placeholder="Search by product, SKU, or work center..."
							size="small"
							value={searchQuery}
							onChange={handleSearch}
							sx={searchFieldSx}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon fontSize="small" sx={{ color: planner.inkMuted }} />
									</InputAdornment>
								),
								endAdornment: searchQuery && (
									<InputAdornment position="end">
										<IconButton size="small" onClick={clearSearch} sx={{ color: planner.inkMuted }}>
											<ClearIcon fontSize="small" />
										</IconButton>
									</InputAdornment>
								),
							}}
						/>
						<GhostButton startIcon={<UploadFileIcon />} onClick={openUpload}>
							Upload
						</GhostButton>
						<PrimaryButton startIcon={<AddIcon />} onClick={form.openNew}>
							New standard
						</PrimaryButton>
					</>
				}
			/>

			{/* Loading */}
			{data.isLoadingAnything && (
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, p: 4 }}>
					<CircularProgress size={22} sx={{ color: planner.navy }} />
					<Typography sx={{ ...planner.type.body, color: planner.inkSecondary }}>Loading...</Typography>
				</Box>
			)}

			{/* No products message */}
			{!data.loadingProducts && data.products.length === 0 && (
				<EmptyState compact title="No products found" body="Add products in Settings → SKUs first." />
			)}

			{/* Empty states */}
			{!data.isLoadingAnything && data.laborStandards.length === 0 && data.products.length > 0 && (
				<EmptyState
					title="No labor standards yet"
					body="Create the first hours recipe for a product, or upload standards from a spreadsheet."
					actionLabel="New standard"
					onAction={form.openNew}
				/>
			)}
			{!data.isLoadingAnything && data.laborStandards.length > 0 && hasSearch && filteredStandards.length === 0 && (
				<EmptyState title={`No standards match "${searchQuery}"`} body="Try a different product name, SKU, or work center." />
			)}

			{/* Standards catalog */}
			{filteredStandards.length > 0 && (
				<>
					<SectionLabel sx={{ mb: 1.25 }}>
						Catalog · {filteredStandards.length} {filteredStandards.length === 1 ? "standard" : "standards"}
					</SectionLabel>
					<Grid2 container spacing={2} sx={{ mb: 4 }}>
						{filteredStandards.map((standard) => (
							<StandardCard
								key={standard.id}
								standard={standard}
								product={standard.productId ? data.productById.get(standard.productId) : undefined}
								isExpanded={expandedOperations[standard.id] || false}
								breakdown={data.roleBreakdownByStandard.get(standard.id)}
								onToggle={toggleExpand}
								onPreview={setPreviewStandard}
								onEdit={form.edit}
								onDelete={deleteStandard}
							/>
						))}
					</Grid2>
				</>
			)}

			<StandardFormDialog
				form={form}
				products={data.products}
				operationTypes={data.operationTypes}
				workcenters={data.workcenters}
				jobRoles={data.jobRoles}
				workPhases={data.workPhases}
				existingForProduct={existingForProduct}
				onPreview={setPreviewStandard}
				onDeleteExisting={deleteStandard}
			/>

			<UploadLaborStandardDialog open={showUploadDialog} onClose={closeUpload} onUploadSuccess={handleUploadSuccess} />

			{previewStandard && <LaborStandardPreviewDialog open={Boolean(previewStandard)} onClose={closePreview} standard={previewStandard} />}

			<Snackbar
				open={data.snackbar.open}
				autoHideDuration={6000}
				onClose={data.closeSnackbar}
				message={data.snackbar.message}
				ContentProps={{
					sx: {
						bgcolor: data.snackbar.severity === "error" ? planner.unstaffed : planner.navy,
						color: planner.paper,
						fontFamily: planner.fontUi,
						fontSize: 13,
						borderRadius: `${planner.radiusSm}px`,
						boxShadow: planner.shadowHover,
					},
				}}
			/>
		</PlannerPage>
	);
};

export default LaborStandards;
